import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useCustomerSelection } from '../context/CustomerSelectionContext';
import { partnerService } from '../services/partnerService';
import { Device, Customer } from '../types/partner';

interface DeviceWithCustomer extends Device {
  customerName: string;
  customerSlug?: string;
}

// Note: Icon components removed as they were unused in final implementation

// Enhanced Progress Bar Component
interface AnimatedProgressBarProps {
  value: number;
  maxValue: number;
  label?: string;
  colorClass?: string;
  showAnimation?: boolean;
  height?: string;
}

const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({ 
  value, 
  maxValue, 
  label, 
  colorClass = 'bg-blue-500',
  showAnimation = true,
  height = 'h-3'
}) => {
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  useEffect(() => {
    if (showAnimation) {
      const timer = setTimeout(() => {
        setAnimatedPercentage(percentage);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAnimatedPercentage(percentage);
    }
  }, [percentage, showAnimation]);
  
  return (
    <div className="w-full">
      {label && (
        <div className="flex justify-between text-xs text-neutral-600 mb-2">
          <span>{label}</span>
          <span>{percentage.toFixed(1)}%</span>
        </div>
      )}
      <div className={`${height} bg-neutral-200 rounded-full overflow-hidden relative`}>
        <div 
          className={`${height} ${colorClass} rounded-full transition-all duration-1000 ease-out relative overflow-hidden`}
          style={{ width: `${animatedPercentage}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

// OS Icon component
const OsIcon = ({ os, platform }: { os?: string; platform?: string }) => {
  const osLower = (os || '').toLowerCase();
  const platformLower = (platform || '').toLowerCase();

  if (osLower.includes('android')) {
    return (
      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">A</div>
    );
  }

  if (osLower.includes('ios')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-neutral-500">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    );
  }

  if (osLower.includes('windows') || platformLower.includes('windows')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-blue-600">
        <path d="M0 3.4L9.5 2v9.5H0V3.4zm10.5-1.6L24 0v11.5H10.5V1.8zM0 12.5h9.5V24L0 22.6V12.5zm10.5 0H24V24l-13.5-1.8V12.5z"/>
      </svg>
    );
  }

  if (osLower.includes('macos') || osLower.includes('mac') || platformLower.includes('macos') || platformLower.includes('mac')) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-neutral-700">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
      </svg>
    );
  }

  return (
    <div className="w-5 h-5 bg-neutral-300 rounded-full flex items-center justify-center text-neutral-600 text-xs">?</div>
  );
};

// Device Source Badge Component
const DeviceSourceBadge = ({ device }: { device: DeviceWithCustomer }) => {
  if (device.source === 'Container' || device.deviceType === 'aitryx') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
        <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        Aitryx Agent
      </span>
    );
  }

  if (device.source === 'Intune' || device.deviceType === 'intune') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-coral-100 text-coral-800 border border-coral-200">
        <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 9 5.16-.74 9-4.45 9-10V7l-10-5z"/>
        </svg>
        Intune Managed
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
      Unknown Source
    </span>
  );
};

const DEVICE_CSV_HEADERS = [
  'Customer ID',
  'Customer Name',
  'Device ID',
  'Device Name',
  'Model',
  'Aitryx Version',
  'Status',
  'Last Seen UTC',
  'Owner Name',
  'Owner Email',
];

const csvValue = (value: unknown): string => {
  const raw = value == null ? '' : String(value);
  return `"${raw.replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`;
};

const downloadCsv = (filename: string, csv: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const getStatusFromLastSeen = (lastSeenUtc?: string): 'Online' | 'Offline' | 'Warning' => {
  if (!lastSeenUtc) return 'Offline';

  const lastSeen = new Date(lastSeenUtc);
  if (Number.isNaN(lastSeen.getTime())) return 'Offline';

  const minutesAgo = (Date.now() - lastSeen.getTime()) / (1000 * 60);
  if (minutesAgo < 5) return 'Online';
  if (minutesAgo < 60) return 'Warning';
  return 'Offline';
};

const buildDeviceCsv = (rows: DeviceWithCustomer[]) => {
  const csvRows = rows.map((d) =>
    [
      d.customerId,
      d.customerName || '',
      d.id,
      d.name,
      d.model || '',
      d.agentVersion || '',
      d.status || getStatusFromLastSeen(d.lastSeenUtc),
      d.lastSeenUtc || '',
      d.currentUserName || '',
      d.currentUserEmail || '',
    ].map(csvValue).join(',')
  );

  return [DEVICE_CSV_HEADERS.map(csvValue).join(','), ...csvRows].join('\n');
};

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (nextIndex < items.length) {
        const currentIndex = nextIndex;
        nextIndex += 1;
        results[currentIndex] = await worker(items[currentIndex], currentIndex);
      }
    })
  );

  return results;
}


export function DevicesView() {
  const [devices, setDevices] = useState<DeviceWithCustomer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [expandedDeviceId, setExpandedDeviceId] = useState<string | null>(null);
  const [expandedDeviceDetails, setExpandedDeviceDetails] = useState<DeviceWithCustomer | null>(null);
  const [isExpandedLoading, setIsExpandedLoading] = useState<boolean>(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  
  const { partner, managedCustomers, hasPartnerAccess } = useAuth();
  const { selectedCustomer } = useCustomerSelection();

  const managedCustomersRef = React.useRef(managedCustomers);
  managedCustomersRef.current = managedCustomers;

  const loadingRef = React.useRef(false);

  // Load customers and devices
  useEffect(() => {
    if (loadingRef.current) return;

    const loadData = async () => {
      if (!partner?.partnerId || !hasPartnerAccess) {
        setIsLoading(false);
        return;
      }

      loadingRef.current = true;
      let customerList: Customer[] = [];
      
      try {
        setIsLoading(true);
        setError(null);

        const currentManagedCustomers = managedCustomersRef.current;
        if (currentManagedCustomers && currentManagedCustomers.length > 0) {
          customerList = currentManagedCustomers;
        } else {
          const customersResponse = await partnerService.getPartnerCustomers(partner.partnerId);
          if (customersResponse.success && customersResponse.data) {
            customerList = customersResponse.data;
          }
        }
        if (selectedCustomer) {
          const devicesResponse = await partnerService.getAllPartnerDevices(partner.partnerId, {
            customerId: selectedCustomer.customerId
          });
          
          if (devicesResponse.success && devicesResponse.data && devicesResponse.data.length > 0) {
            const transformedDevices = devicesResponse.data.map(device => ({
              ...device,
              customerName: selectedCustomer.customerName,
              customerSlug: selectedCustomer.customerId.substring(0, 8)
            }));
            
            setDevices(transformedDevices);
          } else {
            setDevices([]);
            setError(`No devices found for ${selectedCustomer.customerName.trim()}. Check if devices have uploaded telemetry recently.`);
          }
        } else {
          setDevices([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load devices');
        setDevices(getMockDevices(customerList));
      } finally {
        setIsLoading(false);
        loadingRef.current = false;
      }
    };

    loadData();
  }, [partner?.partnerId, hasPartnerAccess, selectedCustomer]);

  // Mock devices for development
  const getMockDevices = (customerList: Customer[]): DeviceWithCustomer[] => {
    if (customerList.length === 0) return [];
    
    return [
      {
        id: '1',
        customerId: customerList[0]?.customerId || 'mock-customer-1',
        name: 'WS-001',
        model: 'Dell Latitude 7420',
        ownerUserId: 'user-001',
        firstSeenUtc: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        lastSeenUtc: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
        lastBlobPath: 'telemetry/device-1/latest.json',
        customerName: customerList[0]?.customerName || 'Mock Customer',
        customerSlug: customerList[0]?.customerId.substring(0, 8),
        agentVersion: '0.8.2'
      },
      {
        id: '2',
        customerId: customerList[0]?.customerId || 'mock-customer-1',
        name: 'SRV-001',
        model: 'HP ProLiant DL380',
        ownerUserId: 'user-002',
        firstSeenUtc: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        lastSeenUtc: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        lastBlobPath: 'telemetry/device-2/latest.json',
        customerName: customerList[0]?.customerName || 'Mock Customer',
        customerSlug: customerList[0]?.customerId.substring(0, 8),
        agentVersion: '0.8.1'
      }
    ];
  };

  // Get device status based on last seen time
  const getDeviceStatus = (lastSeenUtc: string): 'Online' | 'Offline' | 'Warning' => {
    return getStatusFromLastSeen(lastSeenUtc);
  };

  // Format last seen time
  const formatLastSeen = (lastSeenUtc: string): string => {
    const lastSeen = new Date(lastSeenUtc);
    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const formatAitryxVersion = (version?: string | null): string => {
    const trimmed = version?.trim();
    if (!trimmed) return 'Not reported';
    return trimmed.toLowerCase().startsWith('v') ? trimmed : `v${trimmed}`;
  };

  const getAvailableCustomers = async (): Promise<Customer[]> => {
    if (managedCustomersRef.current && managedCustomersRef.current.length > 0) {
      return managedCustomersRef.current;
    }

    if (!partner?.partnerId) return [];

    const customersResponse = await partnerService.getPartnerCustomers(partner.partnerId);
    if (customersResponse.success && customersResponse.data) {
      return customersResponse.data;
    }

    throw new Error(customersResponse.error || 'Failed to load customers');
  };

  const fetchDevicesForCustomers = async (customerList: Customer[]) => {
    const failures: string[] = [];
    const deviceGroups = await mapWithConcurrency(customerList, 6, async (customer) => {
      try {
        const devicesResponse = await partnerService.getAllPartnerDevices(partner!.partnerId, {
          customerId: customer.customerId,
          pageSize: 10000,
        });

        if (!devicesResponse.success || !devicesResponse.data) {
          failures.push(customer.customerName);
          return [];
        }

        return devicesResponse.data.map((device) => ({
          ...device,
          customerId: device.customerId || customer.customerId,
          customerName: customer.customerName,
          customerSlug: customer.customerId.substring(0, 8),
        }));
      } catch (err) {
        failures.push(customer.customerName);
        return [];
      }
    });

    return { devices: deviceGroups.flat(), failures };
  };

  const handleExportAllCustomerDevices = async () => {
    if (!partner?.partnerId || !hasPartnerAccess) return;

    try {
      setIsExportingAll(true);
      setError(null);
      setSuccessMessage(null);

      const customerList = await getAvailableCustomers();
      const { devices: allDevices, failures } = await fetchDevicesForCustomers(customerList);

      if (allDevices.length === 0) {
        setError('No devices found across managed customers.');
        return;
      }

      setDevices(allDevices);
      downloadCsv(`devices-all-customers-${new Date().toISOString().slice(0, 10)}.csv`, buildDeviceCsv(allDevices));

      if (failures.length > 0) {
        setError(`Exported ${allDevices.length} devices, but failed to fetch ${failures.length} customer${failures.length === 1 ? '' : 's'}.`);
      } else {
        setSuccessMessage(`Exported ${allDevices.length} devices from ${customerList.length} customer${customerList.length === 1 ? '' : 's'}.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export devices across customers');
    } finally {
      setIsExportingAll(false);
    }
  };

  // View device details with expandable row
  const viewDeviceDetails = async (device: DeviceWithCustomer) => {
    if (expandedDeviceId === device.id) {
      // If already expanded, collapse it
      setExpandedDeviceId(null);
      setExpandedDeviceDetails(null);
      return;
    }

    // Expand this device
    setExpandedDeviceId(device.id);
    setIsExpandedLoading(true);
    
    try {
      setError(null);
      
      if (device.deviceType === 'aitryx' || device.source === 'Container') {
        // For Aitryx devices, try to get detailed telemetry
        const response = await partnerService.getDeviceDetails(partner!.partnerId, device.customerId, device.id);
        if (response.success && response.data) {
          setExpandedDeviceDetails({ ...device, telemetryData: response.data });
        } else {
          setExpandedDeviceDetails(device); // Fall back to basic device info
        }
      } else {
        // For Intune devices, show basic device details
        setExpandedDeviceDetails(device);
      }
    } catch (err) {
      setExpandedDeviceDetails(device); // Fall back to basic device info
    } finally {
      setIsExpandedLoading(false);
    }
  };

  // Filter devices based on selected customer
  const filteredDevices = !selectedCustomer 
    ? [] 
    : devices.filter(device => device.customerId === selectedCustomer.customerId);

  // Calculate device statistics — match the row's resolution exactly: prefer the backend
  // `device.status` if present, otherwise fall back to last-seen heuristic. Without this, the
  // summary tiles disagreed with the row Status pill (e.g. row showed "Online" while the tile
  // said 0 Online because the heuristic alone classified it Offline).
  const resolveStatus = (d: typeof filteredDevices[number]) => d.status || getDeviceStatus(d.lastSeenUtc);
  const onlineDevices = filteredDevices.filter(d => resolveStatus(d) === 'Online');
  const offlineDevices = filteredDevices.filter(d => resolveStatus(d) === 'Offline');
  const warningDevices = filteredDevices.filter(d => resolveStatus(d) === 'Warning');

  // Clear messages after delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ✅ Enhanced expanded telemetry details component for Aitryx devices
  const AitryxExpandedTelemetryDetails = ({ device }: { device: DeviceWithCustomer }) => {
    const telemetryData = (device as any).telemetryData;

    // Uptime Calculation
    const uptimeSeconds = device.uptimeSeconds ?? telemetryData?.uptimeSeconds ?? 0;
    const formattedUptime = uptimeSeconds && uptimeSeconds > 0
      ? `${Math.floor(uptimeSeconds / 86400)}d ${Math.floor((uptimeSeconds % 86400) / 3600)}h`
      : 'N/A';
    const agentVersion = device.agentVersion ?? telemetryData?.agentVersion;

    if (isExpandedLoading) {
      return (
        <div className="p-8 bg-neutral-50">
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-neutral-200 border-t-blue-500 rounded-full animate-spin mr-3"></div>
            <span className="text-neutral-500">Loading detailed telemetry...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-neutral-50">
        <Card elevation="md" border={true} className="m-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
            {/* Left: Device Overview & Specifications */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-neutral-900">Device Information</h3>
              
              {/* User Information */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="text-xs text-neutral-600 mb-1">Current User</div>
                  <div className="text-base font-medium text-neutral-900">
                    {device.currentUserName || telemetryData?.currentUserName || 'Unknown User'}
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {device.currentUserEmail || telemetryData?.currentUserEmail || 'No email'}
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="text-xs text-neutral-600 mb-1">System Uptime</div>
                  <div className="text-base font-medium text-neutral-900">
                    {formattedUptime}
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-lg p-4">
                  <div className="text-xs text-neutral-600 mb-1">Aitryx Version</div>
                  <div className={agentVersion ? 'text-base font-medium text-neutral-900 tabular-nums' : 'text-base text-neutral-400'}>
                    {formatAitryxVersion(agentVersion)}
                  </div>
                </div>
              </div>

              {/* Hardware Specifications */}
              <div className="w-full rounded-t-lg bg-blue-600 px-6 py-3 mb-3">
                <h4 className="text-xs font-semibold text-white m-0">Hardware Specifications</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Processor</div>
                    <div className="text-base font-medium text-neutral-900">{device.cpuBrand || 'Unknown CPU'}</div>
                    <div className="text-xs text-neutral-500">
                      {device.cpuCores} cores
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Memory</div>
                    <div className="text-base font-medium text-neutral-900">
                      {device.totalMemoryGB ? `${Math.round(device.totalMemoryGB)} GB RAM` : 'Not available'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Serial Number</div>
                    <div className="text-base font-medium text-neutral-900">
                      {device.serialNumber || device.serial || 'Unknown'}
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Manufacturer</div>
                    <div className="text-base font-medium text-neutral-900">
                      {device.manufacturer || device.hardwareVendor || 'Not available'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Model</div>
                    <div className="text-base font-medium text-neutral-900">
                      {device.model || device.hardwareModel || 'Not available'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">OS Version</div>
                    <div className="text-base font-medium text-neutral-900">{device.osVersion || 'Unknown'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: System Status */}
            <div>
              <h3 className="text-lg font-semibold mb-6 text-neutral-900">System Status</h3>
              <div className="space-y-4">
                {/* CPU Usage */}
                <div className="bg-white rounded-lg p-4 border border-neutral-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">CPU Usage</span>
                    <span className="text-xs text-neutral-600">
                      {(device.cpuUsage ?? 0).toFixed(1)}%
                    </span>
                  </div>
                  <AnimatedProgressBar
                    value={device.cpuUsage ?? 0}
                    maxValue={100}
                    colorClass="bg-blue-500"
                    height="h-2"
                  />
                </div>

                {/* Memory Usage */}
                <div className="bg-white rounded-lg p-4 border border-neutral-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">Memory Usage</span>
                    <span className="text-xs text-neutral-600">
                      {(device.memoryUsage || 0).toFixed(1)}%
                    </span>
                  </div>
                  <AnimatedProgressBar
                    value={device.memoryUsage || 0}
                    maxValue={100}
                    colorClass="bg-coral-500"
                    height="h-2"
                  />
                </div>

                {/* Disk Usage */}
                <div className="bg-white rounded-lg p-4 border border-neutral-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium">Disk Usage</span>
                    <span className="text-xs text-neutral-600">
                      {(device.maxDiskUsage || 0).toFixed(1)}%
                    </span>
                  </div>
                  <AnimatedProgressBar
                    value={device.maxDiskUsage || 0}
                    maxValue={100}
                    colorClass="bg-orange-500"
                    height="h-2"
                  />
                </div>

                {/* Health Metrics */}
                <div className="bg-white rounded-lg p-4 border border-neutral-200">
                  <div className="text-xs font-medium mb-3">Health & Status</div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-600">Overall Health Score</span>
                      <span className="text-sm font-medium text-neutral-900">{device.healthScore || 0}/100</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-neutral-600">Customer</span>
                      <span className="text-sm font-medium text-neutral-900">{device.customerName}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  // Expanded device details component for Intune devices
  const IntuneExpandedDeviceDetails = ({ device }: { device: DeviceWithCustomer }) => {
    const osType = (device.os || '').toLowerCase();
    const isAndroid = osType.includes('android');
    
    const DetailRow = ({ label, value }: { label: string; value?: string }) => (
      <div className={`flex justify-between py-3 border-b ${isAndroid ? 'border-green-200' : 'border-neutral-200'}`}>
        <dt className="text-sm text-neutral-600">{label}</dt>
        <dd className="text-sm text-neutral-900 font-medium text-right">{value || 'Not available'}</dd>
      </div>
    );

    const DetailSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
      <div className="mb-6">
        <h3 className={`text-md font-semibold pb-2 mb-2 border-b ${isAndroid ? 'text-green-800 border-green-300' : 'text-neutral-800 border-neutral-300'}`}>{title}</h3>
        <dl>{children}</dl>
      </div>
    );

    return (
      <div className={`p-6 border-l-4 ${isAndroid ? 'bg-green-50 border-green-300' : 'bg-neutral-50 border-neutral-300'}`}>
        {isExpandedLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-neutral-200 border-t-blue-500 rounded-full animate-spin mr-3"></div>
            <span className="text-neutral-500">Loading device details...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <DetailSection title="System">
              <DetailRow label="Management name" value={device.managementName} />
              <DetailRow label="Intune Device ID" value={device.intuneDeviceId} />
              <DetailRow label="Microsoft Entra Device ID" value={device.entraDeviceId} />
              <DetailRow label="Serial number" value={device.serialNumber} />
              <DetailRow label="Customer" value={device.customerName} />
            </DetailSection>

            <DetailSection title="Operating System">
              <DetailRow label="Operating system" value={device.os} />
              <DetailRow label="Operating system version" value={device.osVersion} />
              <DetailRow label="Operating system build number" value={device.osBuildNumber} />
              <DetailRow label="Operating system language" value={device.osLanguage} />
              <DetailRow label="Operating system edition" value={device.osEdition} />
            </DetailSection>

            <DetailSection title="Storage">
              <DetailRow label="Total storage space" value={device.totalStorage} />
              <DetailRow label="Free storage space" value={device.freeStorage} />
              <DetailRow label="Total physical memory" value={device.totalMemory} />
            </DetailSection>

            <DetailSection title="Device Information">
              <DetailRow label="Manufacturer" value={device.manufacturer} />
              <DetailRow label="Model" value={device.model} />
              <DetailRow label="IMEI" value={device.imei} />
              <DetailRow label="Phone number" value={device.phoneNumber} />
            </DetailSection>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
              <svg className="h-5 w-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Device Management</h2>
              <p className="text-sm text-slate-500">
                {selectedCustomer ? `${filteredDevices.length} devices for ${selectedCustomer.customerName}` : 'Select a customer to view devices'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportAllCustomerDevices}
              loading={isExportingAll}
              disabled={!partner?.partnerId || !hasPartnerAccess}
            >
              {isExportingAll ? 'Exporting...' : 'Export all customers'}
            </Button>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Error and Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
            <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* Device Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{onlineDevices.length}</div>
                <div className="text-xs text-slate-500">Online</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{warningDevices.length}</div>
                <div className="text-xs text-slate-500">Warning</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{offlineDevices.length}</div>
                <div className="text-xs text-slate-500">Offline</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                </svg>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{filteredDevices.length}</div>
                <div className="text-xs text-slate-500">Total Devices</div>
              </div>
            </div>
          </div>
        </div>

      {/* Devices Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Devices</h3>
        </div>
        {filteredDevices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Device Name</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Hardware Info</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">OS & Source</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Aitryx Version</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Customer</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Last Seen</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Status</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.map((device) => {
                  const status = device.status || getDeviceStatus(device.lastSeenUtc);
                  return (
                    <React.Fragment key={device.id}>
                      <tr 
                        className={`hover:bg-slate-50/50 transition cursor-pointer ${
                          expandedDeviceId === device.id ? 'bg-slate-50' : ''
                        }`}
                        onClick={() => viewDeviceDetails(device)}
                      >
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div className="flex items-center gap-3">
                            {/* Status Indicator */}
                            <div className="relative">
                              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-500">
                                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                                  <line x1="8" y1="21" x2="16" y2="21"></line>
                                  <line x1="12" y1="17" x2="12" y2="21"></line>
                                </svg>
                              </div>
                              {/* Status Dot */}
                              <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${
                                status === 'Online' ? 'bg-emerald-500' : 
                                status === 'Warning' ? 'bg-amber-500' : 'bg-red-500'
                              }`}></div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-slate-900">{device.name}</div>
                              <div className="text-xs text-slate-500">{device.serialNumber || device.serial || '—'}</div>
                              {(device.currentUserName && device.currentUserEmail) && (
                                <div className="text-xs text-blue-600 mt-0.5">
                                  {device.currentUserName}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div>
                            {(device.deviceType === 'aitryx' || device.source === 'Container') && (
                              <div>
                                <div className="text-sm text-slate-900 font-medium">
                                  {device.manufacturer || device.hardwareVendor || 'Unknown'} {device.model || device.hardwareModel || ''}
                                </div>
                                <div className="text-xs text-slate-500 mt-0.5">
                                  {device.cpuBrand || 'Unknown CPU'}
                                </div>
                              </div>
                            )}
                            {(device.deviceType === 'intune' || device.source === 'Intune') && (
                              <div>
                                <div className="text-sm text-slate-900 font-medium">
                                  {device.model || 'Unknown Model'}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {device.manufacturer || 'Unknown Manufacturer'}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <OsIcon os={device.os || device.operatingSystem} platform={device.platform} />
                              <span className="text-sm text-slate-700">{device.os || device.operatingSystem || device.platform}</span>
                            </div>
                            <DeviceSourceBadge device={device} />
                          </div>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <span className={device.agentVersion ? 'text-sm font-medium text-slate-700 tabular-nums' : 'text-sm text-slate-400'}>
                            {formatAitryxVersion(device.agentVersion)}
                          </span>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div className="text-sm text-slate-900">{device.customerName}</div>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div className="text-sm text-slate-900">{formatLastSeen(device.lastSeenUtc)}</div>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${ 
                            status === 'Online' ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20' :
                            status === 'Warning' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20' :
                            'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                          }`}>
                            {status}
                          </span>
                        </td>
                        <td className="py-3 px-4 border-b border-slate-100">
                          <div className="flex items-center gap-1">
                            <div className={`transition-transform duration-200 ${expandedDeviceId === device.id ? 'rotate-180' : ''}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-400">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </div>
                          </div>
                        </td>
                      </tr>
                      {/* Expandable Row Details */}
                      {expandedDeviceId === device.id && (
                        <tr>
                          <td colSpan={8} className="p-0 border-b border-slate-200">
                            {device.deviceType === 'aitryx' || device.source === 'Container' ? (
                              <AitryxExpandedTelemetryDetails device={expandedDeviceDetails || device} />
                            ) : (
                              <IntuneExpandedDeviceDetails device={expandedDeviceDetails || device} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No devices found</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">
              {selectedCustomer
                ? `No devices found for ${selectedCustomer.customerName.trim()}. Devices will appear once the Aitryx agent is installed.`
                : 'Please select a customer to view their devices.'}
            </p>
          </div>
        )}
      </div>

      </div>
    </div>
  );
}
